import { useCallback, useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

function audienceLabel(b) {
  if (b.audience === "members") return "All members";
  if (b.audience === "subscribers") return "Newsletter subscribers";
  return `RSVPs: ${b.event_title || "event"}`;
}

function Composer({ chapterId, counts, events, draft, onDone }) {
  const empty = { subject: "", body: "", audience: "members", event_id: "" };
  const [form, setForm] = useState(
    draft
      ? {
          subject: draft.subject,
          body: draft.body,
          audience: draft.audience,
          event_id: draft.event_id || "",
        }
      : empty
  );
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const recipientHint =
    form.audience === "members"
      ? `${counts.members} member${counts.members === 1 ? "" : "s"} with an email`
      : form.audience === "subscribers"
        ? `${counts.subscribers} subscriber${counts.subscribers === 1 ? "" : "s"} (unsubscribe footer added)`
        : form.event_id
          ? `${events.find((e) => e.id === Number(form.event_id))?.rsvps ?? 0} RSVP email(s)`
          : "pick an event";

  async function submit(e) {
    e.preventDefault();
    setError("");
    const res = draft
      ? await api(`/api/broadcasts/${draft.id}`, { method: "PATCH", body: JSON.stringify(form) })
      : await api(`/api/chapters/${chapterId}/broadcasts`, {
          method: "POST",
          body: JSON.stringify(form),
        });
    if (res.ok) {
      setForm(empty);
      onDone();
    } else {
      setError((await res.json().catch(() => ({}))).error || "Could not save the draft.");
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Audience
          <select name="audience" value={form.audience} onChange={set("audience")}>
            <option value="members">Members ({counts.members})</option>
            <option value="subscribers">Subscribers ({counts.subscribers})</option>
            <option value="event_rsvps">Event RSVPs</option>
          </select>
        </label>
        {form.audience === "event_rsvps" && (
          <label className="field">
            Event
            <select name="event_id" required value={form.event_id} onChange={set("event_id")}>
              <option value="">Choose an event…</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title} ({e.rsvps})
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field" style={{ gridColumn: "span 2" }}>
          Subject
          <input name="subject" required value={form.subject} onChange={set("subject")} placeholder="July chapter update" />
        </label>
      </div>
      <label className="field" style={{ marginTop: 10, display: "grid" }}>
        Message (plain text)
        <textarea name="body" required value={form.body} onChange={set("body")} rows={6} placeholder="What's happening this month…" />
      </label>
      <div className="field-actions">
        <button className="btn-primary" style={{ width: "auto" }} type="submit">
          {draft ? "Save changes" : "Save draft"}
        </button>
        {draft && (
          <button className="btn-ghost" type="button" onClick={() => onDone()}>
            Cancel
          </button>
        )}
        <span className="muted" style={{ fontSize: 13 }}>
          Will go to: {recipientHint}
        </span>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

export function Broadcasts({ chapterId, canManage }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    const res = await api(`/api/chapters/${chapterId}/broadcasts`);
    if (res.ok) setData(await res.json());
  }, [chapterId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (data === null) return <p className="muted">Loading…</p>;

  const drafts = data.broadcasts.filter((b) => b.status === "draft");
  const sent = data.broadcasts.filter((b) => b.status === "sent");

  async function send(b) {
    if (!window.confirm(`Send "${b.subject}" to ${audienceLabel(b).toLowerCase()} now?`)) return;
    const res = await api(`/api/broadcasts/${b.id}/send`, { method: "POST" });
    if (!res.ok) {
      window.alert((await res.json().catch(() => ({}))).error || "Send failed.");
    }
    reload();
  }

  async function remove(b) {
    if (!window.confirm(`Delete the draft "${b.subject}"?`)) return;
    await api(`/api/broadcasts/${b.id}`, { method: "DELETE" });
    reload();
  }

  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Email announcements and newsletters — to the member roster, the public
        subscriber list, or an event's RSVPs. Subscriber emails carry an unsubscribe
        link automatically.
      </p>

      {canManage && !editing && (
        <div className="section">
          <div className="section-head">
            <h3>Compose</h3>
          </div>
          <Composer
            chapterId={chapterId}
            counts={data.audience_counts}
            events={data.events}
            draft={null}
            onDone={reload}
          />
        </div>
      )}

      {drafts.length > 0 && (
        <div className="section">
          <div className="section-head">
            <h3>Drafts</h3>
          </div>
          {drafts.map((b) =>
            editing === b.id ? (
              <Composer
                key={b.id}
                chapterId={chapterId}
                counts={data.audience_counts}
                events={data.events}
                draft={b}
                onDone={() => {
                  setEditing(null);
                  reload();
                }}
              />
            ) : (
              <div key={b.id} className="form-card" style={{ marginTop: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <strong style={{ flex: 1 }}>{b.subject}</strong>
                  <span className="pill">{audienceLabel(b)}</span>
                  {canManage && (
                    <span>
                      <button className="btn-primary" style={{ width: "auto", padding: "6px 12px", fontSize: 13 }} onClick={() => send(b)} type="button">
                        Send now
                      </button>
                      <button className="btn-ghost" style={{ marginLeft: 6, padding: "6px 12px", fontSize: 13 }} onClick={() => setEditing(b.id)} type="button">
                        Edit
                      </button>
                      <button className="btn-danger" style={{ marginLeft: 6, padding: "6px 12px", fontSize: 13 }} onClick={() => remove(b)} type="button">
                        Delete
                      </button>
                    </span>
                  )}
                </div>
                <p className="muted" style={{ fontSize: 13, margin: "6px 0 0", whiteSpace: "pre-wrap" }}>
                  {b.body.length > 240 ? `${b.body.slice(0, 240)}…` : b.body}
                </p>
              </div>
            )
          )}
        </div>
      )}

      <div className="section">
        <div className="section-head">
          <h3>Send log</h3>
        </div>
        {sent.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Sent</th>
                <th>Subject</th>
                <th>Audience</th>
                <th>Recipients</th>
                <th>Sent by</th>
              </tr>
            </thead>
            <tbody>
              {sent.map((b) => (
                <tr key={b.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{(b.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{b.subject}</td>
                  <td>{audienceLabel(b)}</td>
                  <td>
                    {b.recipient_count}
                    {b.failed_count ? <span style={{ color: "#b42318" }}> ({b.failed_count} failed)</span> : null}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {b.sent_by}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty">Nothing sent yet.</p>
        )}
      </div>
    </>
  );
}
